import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';
import { runtimeConfig } from '@/config';

type FeatureKey = keyof AppFeatureFlags;

function hasAnyGroup(groups: string[], required: string[]): boolean {
  return required.some((requiredGroup) => groups.includes(requiredGroup));
}

function canAccessCarparkControl(auth: ReturnType<typeof useAuthStore>): boolean {
  return auth.isAdmin || hasAnyGroup(auth.groups, ['barrier-operator', 'barrier-controller']);
}

function canAccessCarparkManager(auth: ReturnType<typeof useAuthStore>): boolean {
  return auth.isAdmin || hasAnyGroup(auth.groups, ['barrier-controller']);
}

function canAccessContacts(auth: ReturnType<typeof useAuthStore>): boolean {
  return auth.isAdmin || hasAnyGroup(auth.groups, ['elder']);
}

function canAccessCookbook(auth: ReturnType<typeof useAuthStore>): boolean {
  return auth.isAdmin || hasAnyGroup(auth.groups, ['cookbook']);
}

function resolveDefaultPath(auth: ReturnType<typeof useAuthStore>): string {
  const { featureFlags } = runtimeConfig;

  if (featureFlags.contacts && canAccessContacts(auth)) return '/contacts';
  if (featureFlags.districts && auth.isAdmin) return '/districts';
  if (featureFlags.ministries && auth.isAdmin) return '/ministries';
  if (featureFlags.auditLog && auth.isAdmin) return '/audit-log';
  if (featureFlags.carparkControl && canAccessCarparkControl(auth)) return '/carpark/control';
  if (featureFlags.carparkLog && canAccessCarparkManager(auth)) return '/carpark/log';
  if (featureFlags.carparkPhoneNumbers && canAccessCarparkManager(auth)) return '/carpark/phone-numbers';
  // Last in the ladder, but present: a cookbook project leader with no other role must land
  // somewhere useful rather than on the changelog.
  if (featureFlags.cookbook && canAccessCookbook(auth)) return '/cookbook';

  return '/changelog';
}

function resolveCarparkDefaultPath(auth: ReturnType<typeof useAuthStore>): string {
  const { featureFlags } = runtimeConfig;

  if (featureFlags.carparkControl && canAccessCarparkControl(auth)) return '/carpark/control';
  if (featureFlags.carparkLog && canAccessCarparkManager(auth)) return '/carpark/log';
  if (featureFlags.carparkPhoneNumbers && canAccessCarparkManager(auth)) return '/carpark/phone-numbers';

  return resolveDefaultPath(auth);
}

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/login', name: 'login', component: () => import('@/pages/LoginPage.vue'), meta: { public: true } },
    { path: '/callback', name: 'callback', component: () => import('@/pages/CallbackPage.vue'), meta: { public: true } },
    {
      path: '/',
      component: () => import('@/components/layout/AppLayout.vue'),
      children: [
        {
          path: '',
          redirect: () => {
            const auth = useAuthStore();
            return resolveDefaultPath(auth);
          },
        },
        {
          path: 'contacts',
          name: 'contacts',
          component: () => import('@/pages/contacts/ContactsListPage.vue'),
          meta: { featureKey: 'contacts' as FeatureKey, requiresAnyGroup: ['admin', 'elder'] },
        },
        {
          path: 'contacts/new',
          name: 'contacts-new',
          component: () => import('@/pages/contacts/ContactFormPage.vue'),
          meta: { featureKey: 'contacts' as FeatureKey, requiresAnyGroup: ['admin', 'elder'] },
        },
        {
          path: 'contacts/:id',
          name: 'contact-detail',
          component: () => import('@/pages/contacts/ContactDetailPage.vue'),
          meta: { featureKey: 'contacts' as FeatureKey, requiresAnyGroup: ['admin', 'elder'] },
        },
        {
          path: 'contacts/:id/edit',
          name: 'contacts-edit',
          component: () => import('@/pages/contacts/ContactFormPage.vue'),
          meta: { featureKey: 'contacts' as FeatureKey, requiresAnyGroup: ['admin', 'elder'] },
        },
        {
          path: 'districts',
          name: 'districts',
          component: () => import('@/pages/districts/DistrictsPage.vue'),
          meta: { adminOnly: true, featureKey: 'districts' as FeatureKey },
        },
        {
          path: 'ministries',
          name: 'ministries',
          component: () => import('@/pages/ministries/MinistriesPage.vue'),
          meta: { adminOnly: true, featureKey: 'ministries' as FeatureKey },
        },
        {
          path: 'audit-log',
          name: 'audit-log',
          component: () => import('@/pages/audit/AuditLogPage.vue'),
          meta: { adminOnly: true, featureKey: 'auditLog' as FeatureKey },
        },
        {
          path: 'users',
          name: 'user-management',
          component: () => import('@/pages/users/UserManagementPage.vue'),
          meta: { adminOnly: true, featureKey: 'userManagement' as FeatureKey },
        },
        { path: 'changelog', name: 'changelog', component: () => import('@/pages/changelog/ChangelogPage.vue') },
        // Carpark
        {
          path: 'carpark',
          redirect: () => {
            const auth = useAuthStore();
            return resolveCarparkDefaultPath(auth);
          },
        },
        {
          path: 'carpark/control',
          name: 'carpark-control',
          component: () => import('@/pages/carpark/CarParkControlPage.vue'),
          meta: {
            requiresAnyGroup: ['barrier-operator', 'barrier-controller'],
            featureKey: 'carparkControl' as FeatureKey,
          },
        },
        {
          path: 'carpark/phone-numbers',
          name: 'carpark-phone-numbers',
          component: () => import('@/pages/carpark/PhoneNumbersPage.vue'),
          meta: { requiresAnyGroup: ['barrier-controller'], featureKey: 'carparkPhoneNumbers' as FeatureKey },
        },
        {
          path: 'carpark/log',
          name: 'carpark-log',
          component: () => import('@/pages/carpark/BarrierLogPage.vue'),
          meta: { requiresAnyGroup: ['barrier-controller'], featureKey: 'carparkLog' as FeatureKey },
        },
        // Cookbook
        {
          path: 'cookbook',
          name: 'cookbook',
          component: () => import('@/pages/cookbook/CookbookDashboardPage.vue'),
          meta: { requiresAnyGroup: ['cookbook'], featureKey: 'cookbook' as FeatureKey },
        },
        {
          path: 'cookbook/recipes',
          name: 'cookbook-recipes',
          component: () => import('@/pages/cookbook/RecipeListPage.vue'),
          meta: { requiresAnyGroup: ['cookbook'], featureKey: 'cookbook' as FeatureKey },
        },
        {
          path: 'cookbook/recipes/:id',
          name: 'cookbook-recipe',
          component: () => import('@/pages/cookbook/RecipeDetailPage.vue'),
          meta: { requiresAnyGroup: ['cookbook'], featureKey: 'cookbook' as FeatureKey },
        },
        {
          path: 'cookbook/book',
          name: 'cookbook-book',
          component: () => import('@/pages/cookbook/BookPlanPage.vue'),
          meta: { requiresAnyGroup: ['cookbook'], featureKey: 'cookbook' as FeatureKey },
        },
        {
          path: 'cookbook/archive',
          name: 'cookbook-archive',
          component: () => import('@/pages/cookbook/ArchivePage.vue'),
          meta: { requiresAnyGroup: ['cookbook'], featureKey: 'cookbook' as FeatureKey },
        },
      ],
    },
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
});

// ── Navigation guard ──────────────────────────────────────────────────────────
router.beforeEach((to) => {
  if (to.meta.public) return true;

  const auth = useAuthStore();
  if (!auth.isAuthenticated) {
    return { name: 'login' };
  }

  const featureKey = to.meta.featureKey as FeatureKey | undefined;
  if (featureKey && !runtimeConfig.featureFlags[featureKey]) {
    return resolveDefaultPath(auth);
  }

  // Admin-only routes
  if (to.meta.adminOnly && !auth.isAdmin) {
    console.warn('Access denied: user is not an admin');
    return resolveDefaultPath(auth);
  }

  // Group-restricted routes
  const requiredGroups = to.meta.requiresAnyGroup as string[] | undefined;
  if (requiredGroups?.length) {
    if (!auth.isAdmin && !requiredGroups.some((g) => auth.groups.includes(g))) {
      console.warn(`Access denied: requires one of [${requiredGroups.join(', ')}]`);
      return resolveDefaultPath(auth);
    }
  }

  return true;
});
